import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import ScheduleSettings from './ScheduleSettings'

function PriceInput({ item, canEdit, onBlur }) {
  const [val, setVal] = useState(item.price || '')
  return (
    <div style={styles.priceInputWrap}>
      <span style={styles.dollar}>$</span>
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onBlur(item.id, val)}
        style={styles.priceInput}
        type="text"
        inputMode="numeric"
        disabled={!canEdit}
        placeholder="0"
      />
    </div>
  )
}

export default function PricingPage() {
  const { profile } = useAuth()
  const [pricing, setPricing] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [timeStandards, setTimeStandards] = useState([])
  const [newStd, setNewStd] = useState({ job_type: 'install_aboveground', pool_size: '', expected_hours: '' })
  const role = profile?.role
  const canEdit = role === 'owner' || role === 'office_staff'

  const fetchPricing = useCallback(async () => {
    var [pRes, tRes] = await Promise.all([
      supabase.from('pricing').select('*').order('category').order('size_label'),
      supabase.from('pool_time_standards').select('*').order('job_type').order('pool_size')
    ])
    setPricing(pRes.data || [])
    setTimeStandards(tRes.data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchPricing() }, [fetchPricing])

  const updatePrice = (id, value) => {
    setPricing(p => p.map(item => item.id === id ? { ...item, price: value } : item))
  }

  const handleSave = async () => {
    setSaving(true)
    for (const item of pricing) {
      await supabase.from('pricing').update({ price: parseFloat(item.price) || 0, updated_at: new Date().toISOString() }).eq('id', item.id)
    }
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const getByCategory = (cat) => pricing.filter(p => p.category === cat)

  const PriceSection = ({ title, category }) => (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={styles.priceGrid}>
        {getByCategory(category).map(item => (
          <div key={item.id} style={styles.priceRow}>
            <div style={styles.priceLabel}>{item.size_label}</div>
            <PriceInput item={item} canEdit={canEdit} onBlur={updatePrice} />
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>PRICING</div>
          <div style={styles.subtitle}>CP Quality Pools - installation and liner prices</div>
        </div>
        {canEdit && (
          <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
            {saving ? 'SAVING...' : saved ? 'SAVED!' : 'SAVE ALL PRICES'}
          </button>
        )}
      </div>
      {loading ? <div style={styles.empty}>Loading pricing...</div> : (
        <>
          <div style={styles.twoCol}>
            <PriceSection title="ROUND POOL - FULL INSTALL" category="round_install" />
            <PriceSection title="ROUND POOL - LINER ONLY" category="round_liner" />
          </div>
          <div style={styles.twoCol}>
            <PriceSection title="OVAL POOL - FULL INSTALL" category="oval_install" />
            <PriceSection title="OVAL POOL - LINER ONLY" category="oval_liner" />
          </div>
          <PriceSection title="EXTRAS & ADD-ONS" category="extra" />
          <div style={styles.twoCol}>
            <PriceSection title="ABOVE GROUND - DECK & TOP RAIL" category="ag_deck" />
            <PriceSection title="ABOVE GROUND - INSTALL EXTRAS" category="ag_extra" />
          </div>
          <div style={styles.twoCol}>
            <PriceSection title="INGROUND LINER - EXTRAS" category="ig_liner_extra" />
            <PriceSection title="FIBERGLASS - EXTRAS" category="fiberglass_extra" />
          </div>
          <PriceSection title="LANDSCAPING" category="landscape" />
          <ScheduleSettings />
          {/* Time Standards */}
          <div style={{marginTop:'32px'}}>
            <div style={styles.sectionTitle}>POOL TIME STANDARDS</div>
            <div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',marginBottom:'16px'}}>Set expected hours per pool type/size for crew scoreboard</div>
            {timeStandards.map(function(std) {
              return <div key={std.id} style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'4px',padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px'}}>
                <span style={{flex:1,fontSize:'13px',color:'rgba(255,255,255,0.6)'}}>{std.job_type.replace(/_/g,' ')}</span>
                <span style={{flex:1,fontSize:'13px',color:'#ffffff',fontWeight:500}}>{std.pool_size}</span>
                <input type="number" value={std.expected_hours||''} onChange={async function(e){var v=parseFloat(e.target.value)||0;setTimeStandards(function(prev){return prev.map(function(s){return s.id===std.id?Object.assign({},s,{expected_hours:v}):s})});await supabase.from('pool_time_standards').update({expected_hours:v}).eq('id',std.id)}} style={{width:'80px',padding:'6px 8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#b48c3c',fontSize:'14px',fontWeight:700,textAlign:'center',outline:'none'}} step="0.5" />
                <span style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>hrs</span>
                {canEdit && <button onClick={async function(){if(!window.confirm('Delete?'))return;await supabase.from('pool_time_standards').delete().eq('id',std.id);setTimeStandards(function(p){return p.filter(function(s){return s.id!==std.id})})}} style={{padding:'3px 8px',backgroundColor:'rgba(224,92,92,0.1)',border:'1px solid rgba(224,92,92,0.2)',borderRadius:'2px',color:'#e05c5c',fontSize:'10px',cursor:'pointer'}}>X</button>}
              </div>
            })}
            {canEdit && <div style={{display:'flex',gap:'8px',marginTop:'12px',alignItems:'flex-end'}}>
              <div><div style={{fontSize:'9px',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'2px',color:'rgba(255,255,255,0.25)',marginBottom:'4px'}}>TYPE</div><select value={newStd.job_type} onChange={function(e){setNewStd(Object.assign({},newStd,{job_type:e.target.value}))}} style={{padding:'8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'13px',outline:'none'}}><option value="install_aboveground">Install Aboveground</option><option value="liner_replacement">Liner Replacement</option><option value="liner_inground">Liner Inground</option></select></div>
              <div><div style={{fontSize:'9px',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'2px',color:'rgba(255,255,255,0.25)',marginBottom:'4px'}}>SIZE</div><input value={newStd.pool_size} onChange={function(e){setNewStd(Object.assign({},newStd,{pool_size:e.target.value}))}} style={{padding:'8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'13px',outline:'none',width:'120px'}} placeholder="e.g. 24 round" /></div>
              <div><div style={{fontSize:'9px',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'2px',color:'rgba(255,255,255,0.25)',marginBottom:'4px'}}>HOURS</div><input type="number" value={newStd.expected_hours} onChange={function(e){setNewStd(Object.assign({},newStd,{expected_hours:e.target.value}))}} style={{padding:'8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#fff',fontSize:'13px',outline:'none',width:'80px'}} step="0.5" placeholder="0" /></div>
              <button onClick={async function(){if(!newStd.pool_size)return;var {data}=await supabase.from('pool_time_standards').insert({job_type:newStd.job_type,pool_size:newStd.pool_size,expected_hours:parseFloat(newStd.expected_hours)||0}).select().single();if(data){setTimeStandards(function(p){return[...p,data]});setNewStd({job_type:'install_aboveground',pool_size:'',expected_hours:''})}}} style={{padding:'8px 16px',backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'}}>+ ADD</button>
            </div>}
          </div>
        </>
      )}
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  saveBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 24px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' },
  section: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '24px', marginBottom: '24px' },
  sectionTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '3px', color: 'rgba(255,255,255,0.3)', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  priceGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  priceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  priceLabel: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', flex: 1 },
  priceInputWrap: { display: 'flex', alignItems: 'center', gap: '6px' },
  dollar: { fontSize: '15px', color: 'rgba(255,255,255,0.4)' },
  priceInput: { width: '110px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 12px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow',sans-serif", outline: 'none', textAlign: 'right' },
}
